import type { SchemaIndex } from "@gjwtrack/query-language";

export interface Suggestion {
  /** Text to insert in place of the current token. */
  insert: string;
  label: string;
  hint?: string;
}

const KEYWORDS = ["#Unresolved", "#Resolved", "sort by:", "me", "or"];
const BUILTIN_ATTRS = ["project:", "for:", "by:", "reporter:", "tag:", "watcher:", "created:", "updated:", "resolved:", "number:", "summary:", "description:"];
const DATE_WORDS = ["Today", "Yesterday", "{This week}", "{Last week}", "{This month}"];

/** Braces for names with spaces, as the query language expects. */
export const quoteName = (s: string): string => (/[\s:,]/.test(s) ? `{${s}}` : s);

/**
 * YouTrack-style completion computed from the schema in the browser: at an
 * attribute position offer attributes and keywords; after `attr:` offer that
 * attribute's values. Returns the replacement span too.
 */
export function suggest(schema: SchemaIndex, text: string, caret: number, currentUserId: number): { items: Suggestion[]; start: number; end: number } {
  const before = text.slice(0, caret);
  // Value position: "…attr: partial" where attr is the last attribute before the caret.
  const m = /(?:^|\s)(\{[^}]+\}|[^\s:]+):\s*([^\s,]*)$/.exec(before);
  const rank = (items: Suggestion[], partial: string): Suggestion[] => {
    const p = partial.toLowerCase();
    return items
      .filter((s) => !p || s.label.toLowerCase().includes(p))
      .sort((a, b) => Number(!a.label.toLowerCase().startsWith(p)) - Number(!b.label.toLowerCase().startsWith(p)) || a.label.localeCompare(b.label))
      .slice(0, 12);
  };
  if (m) {
    const attr = m[1]!.replace(/^\{|\}$/g, "").toLowerCase();
    const partial = m[2] ?? "";
    const start = caret - partial.length;
    const values: Suggestion[] = [];
    const push = (label: string, hint?: string) => values.push({ insert: quoteName(label), label, ...(hint ? { hint } : {}) });
    if (attr === "project") for (const p of schema.data.projects) push(p.key, p.name);
    else if (attr === "tag") {
      push("Star", "issues you watch");
      for (const t of schema.data.tags) push(t.name);
    } else if (attr === "reporter" || attr === "watcher" || attr === "by" || attr === "for") {
      push("me");
      for (const u of schema.data.users) push(u.login.split("@")[0] ?? u.login, u.name);
    } else if (["created", "updated", "resolved"].includes(attr)) for (const d of DATE_WORDS) values.push({ insert: d, label: d });
    else {
      const def = schema.fieldsByName.get(attr);
      if (def) {
        if (def.bundleId !== undefined) for (const el of schema.elementsByBundle.get(def.bundleId) ?? []) push(el.name);
        else if (def.kind === "user") {
          push("me");
          for (const u of schema.data.users) push(u.login.split("@")[0] ?? u.login, u.name);
        } else if (def.kind === "date" || def.kind === "datetime") for (const d of DATE_WORDS) values.push({ insert: d, label: d });
      }
    }
    return { items: rank(values, partial), start, end: caret };
  }
  // Attribute / keyword position.
  const w = /(\S*)$/.exec(before);
  const partial = w?.[1] ?? "";
  const start = caret - partial.length;
  const items: Suggestion[] = [];
  const seen = new Set<string>();
  for (const f of schema.data.fields) {
    const label = `${quoteName(f.name)}:`;
    if (!seen.has(label.toLowerCase())) {
      seen.add(label.toLowerCase());
      items.push({ insert: `${label} `, label, hint: f.kind });
    }
  }
  for (const pf of schema.data.projectFields) {
    if (!pf.alias) continue;
    const label = `${quoteName(pf.alias)}:`;
    if (!seen.has(label.toLowerCase())) {
      seen.add(label.toLowerCase());
      items.push({ insert: `${label} `, label, hint: "alias" });
    }
  }
  for (const a of BUILTIN_ATTRS) items.push({ insert: `${a} `, label: a });
  for (const k of KEYWORDS) items.push({ insert: `${k} `, label: k });
  if (partial.startsWith("#")) {
    // #Value shortcuts for option sets.
    for (const f of schema.data.fields) if (f.bundleId !== undefined) for (const el of schema.elementsByBundle.get(f.bundleId) ?? []) items.push({ insert: `#${quoteName(el.name)} `, label: `#${el.name}`, hint: f.name });
  }
  void currentUserId;
  return { items: rank(items, partial), start, end: caret };
}
